import * as Sentry from "@sentry/react";

/**
 * Die einzige Datei, die `@sentry/react` kennt — erzwungen durch
 * `spec/javascript/config/sentry-boundary.test.ts`. Alles andere spricht
 * `captureException` aus diesem Modul.
 *
 * Konfiguration kommt als Meta-Tags aus dem Rails-Layout (`sentry-dsn`,
 * `sentry-environment`), derselbe Weg wie `csp-nonce`: kein Meta, kein
 * Monitoring — dev/test bleiben automatisch still, wie beim Backend-Sentry.
 * Der DSN ist ENV (`SENTRY_FRONTEND_DSN`), kein Credential, und geht bewusst
 * nie über `import.meta.env` (das backt ihn ins öffentliche Bundle).
 */

/**
 * Fehler-Kontext: Status, Operation, Place-IDs — nie Nutzereingaben. Der
 * Suchtext gehört ausdrücklich nicht hinein.
 */
export type CaptureContext = Record<string, string | number | boolean | null>;

export type MonitoringInitOptions = {
  dsn: string;
  environment: string | undefined;
  // Build identifier from the `sentry-release` meta (the Inertia asset
  // version). Without it, every browser event lands as `release: null` and
  // cannot be attributed to a deploy.
  release: string | undefined;
  tracesSampleRate: number;
  dataCollection: RestrictiveDataCollection;
  beforeSend: <Event extends { request?: { url?: string } }>(event: Event) => Event;
  beforeBreadcrumb: (breadcrumb: Sentry.Breadcrumb) => Sentry.Breadcrumb;
};

/**
 * Sends paths only, never query strings. The browser SDK attaches the full
 * page URL to error events and navigation breadcrumbs ungated by
 * `dataCollection`, and the auth pages carry the Rodauth key as a query
 * parameter. Values without a query string are unaffected.
 */
function stripQueryString(url: string): string {
  return url.split("?")[0];
}

function stripUrlQuery<Event extends { request?: { url?: string } }>(event: Event): Event {
  if (!event.request?.url) {
    return event;
  }

  return {
    ...event,
    request: { ...event.request, url: stripQueryString(event.request.url) },
  };
}

/**
 * Sentry's default navigation breadcrumbs record the page URL (path plus
 * query string) in `data.from` and `data.to` — the SDK builds them via
 * `parseUrl(...).relative` — so an auth page's Rodauth key would reach the
 * ingest on a second channel beside `event.request.url`. Breadcrumbs without
 * string `from`/`to` (e.g. fetch crumbs, whose URLs are already sanitized
 * into their own `url`/`url.query` fields) pass through unchanged.
 */
function stripBreadcrumbUrls(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb {
  if (!breadcrumb.data) {
    return breadcrumb;
  }

  const { from, to } = breadcrumb.data;

  return {
    ...breadcrumb,
    data: {
      ...breadcrumb.data,
      ...(typeof from === "string" ? { from: stripQueryString(from) } : {}),
      ...(typeof to === "string" ? { to: stripQueryString(to) } : {}),
    },
  };
}

/**
 * Restrictive collection baseline, explicit since @sentry/react 11: an
 * unset `dataCollection` now collects user info, cookies and query params
 * by default (in v10 the unset `sendDefaultPii` behaved restrictive). The
 * browser context contains user input, so the categories are switched off
 * here instead of trusting defaults. Guard: `monitoring.test.ts`.
 *
 * Scope note: this pins the option's categories. Error events still carry
 * the full page URL including query string in `event.request.url` and in
 * navigation breadcrumbs — the browser SDK attaches them ungated by
 * `dataCollection`, exactly as in v10, so `beforeSend` and `beforeBreadcrumb`
 * below strip the query string.
 *
 * Structural subset of Sentry's `DataCollection` option — the type is not
 * re-exported by `@sentry/react`, and the transitive `@sentry/core` is not
 * a direct dependency.
 */
type RestrictiveDataCollection = {
  userInfo: boolean;
  cookies: boolean;
  httpHeaders: boolean;
  httpBodies: [];
  urlQueryParams: boolean;
  graphQL: { document: boolean; variables: boolean };
  genAI: { inputs: boolean; outputs: boolean };
  databaseQueryData: boolean;
  queues: boolean;
};

const RESTRICTIVE_DATA_COLLECTION: RestrictiveDataCollection = {
  userInfo: false,
  cookies: false,
  httpHeaders: false,
  httpBodies: [],
  urlQueryParams: false,
  graphQL: { document: false, variables: false },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
};

type Reporter = (error: unknown, context?: CaptureContext) => void;

let reporter: Reporter | null = null;

/**
 * Testnaht im Geist der Fake-Client-Philosophie des Branches: Tests setzen
 * einen Recorder, statt `@sentry/react` zu mocken. Muss nach dem Test wieder
 * auf `null` — `bun test` fährt alle Dateien in einem Prozess.
 */
export function setMonitoringReporter(next: Reporter | null): void {
  reporter = next;
}

/**
 * Reads the metas itself and is a no-op without a DSN or without a browser.
 * Errors only, no performance: `tracesSampleRate: 0`, no replay, and the
 * restrictive `dataCollection` baseline — unlike the backend Sentry,
 * deliberately: the browser context contains user input. Since
 * @sentry/react 11 that posture cannot stay on the default; it is pinned
 * in `RESTRICTIVE_DATA_COLLECTION`.
 *
 * `init` is injectable so the test can observe the wiring without running
 * a real `Sentry.init` (which installs global error handlers and console
 * instrumentation no test can tear down). Production calls it without an
 * argument.
 */
export function initMonitoring(
  init: (options: MonitoringInitOptions) => unknown = Sentry.init,
): void {
  if (typeof document === "undefined") {
    return;
  }

  const dsn = document.querySelector<HTMLMetaElement>('meta[name="sentry-dsn"]')?.content ?? "";

  if (!dsn) {
    return;
  }

  const environment =
    document.querySelector<HTMLMetaElement>('meta[name="sentry-environment"]')?.content ||
    undefined;

  const release =
    document.querySelector<HTMLMetaElement>('meta[name="sentry-release"]')?.content || undefined;

  init({
    dsn,
    environment,
    release,
    tracesSampleRate: 0,
    dataCollection: RESTRICTIVE_DATA_COLLECTION,
    beforeSend: stripUrlQuery,
    beforeBreadcrumb: stripBreadcrumbUrls,
  });

  reporter = (error, context) =>
    Sentry.captureException(error, context ? { extra: context } : undefined);
}

/**
 * No-op solange nicht initialisiert — damit überall aufrufbar, auch unter SSR
 * und in Tests ohne Recorder.
 */
export function captureException(error: unknown, context?: CaptureContext): void {
  reporter?.(error, context);
}
